import { setLocale, useTheme, type UiLocale, type UiThemePreference } from '@fcms/ui'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { api } from '@/api/client'

/**
 * Shell preferences: theme, locale, and the site the whole application is
 * looking at.
 *
 * Theme and locale are persisted locally AND, per CLAUDE.md, on the profile
 * server-side. There is no `/me/preferences` operation in
 * `api/openapi/backoffice.yaml` today, so `syncToProfile` is written against
 * the path CLAUDE.md names and is switched off by `PROFILE_SYNC_ENABLED`
 * below rather than firing 404s at a server that has no such route. Flip the
 * constant when the endpoint lands; nothing else changes.
 */

const LOCALE_KEY = 'fcms.locale'
const SITE_KEY = 'fcms.backoffice.site'

/**
 * Server-side preference persistence is off until the operation exists.
 * See the note above — this is a switch, not a placeholder: the request body
 * and path below are the ones CLAUDE.md specifies.
 */
const PROFILE_SYNC_ENABLED = false

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // A preference is not worth failing a page over.
  }
}

export const useUiStore = defineStore('ui', () => {
  const { theme, resolved, isDark, setTheme, cycleTheme } = useTheme()

  const locale = ref<UiLocale>(readStored(LOCALE_KEY) === 'dv' ? 'dv' : 'en')
  // The composable is the single writer of <html lang>/<html dir>; applying it
  // here at construction keeps the attribute the index.html bootstrap set and
  // the store's own value from drifting apart.
  setLocale(locale.value)

  /**
   * The active collection point, or `null` for "every site I can see".
   *
   * Held here rather than in each domain store because it is a property of the
   * session, not of a screen: a supervisor who switches to Kooddoo expects the
   * sessions list, the queue board and the bills list to follow.
   */
  const siteId = ref<string | null>(readStored(SITE_KEY))

  const siteFilter = computed<string | undefined>(() => siteId.value ?? undefined)

  function setSite(next: string | null): void {
    siteId.value = next
    writeStored(SITE_KEY, next)
  }

  /**
   * Defaults the active site for a site-restricted principal.
   *
   * The collection module hard-requires `collection_point_id` for a
   * site-restricted principal (unlike reporting, which silently narrows) —
   * so a supervisor who can only ever see their own site must never land on
   * a screen with no site chosen. Called once the signed-in user's scope is
   * known (see the router guard). A no-op for an unrestricted user, and a
   * no-op when the currently-selected site is still one the user can see
   * (e.g. restored from a previous session on the same device).
   */
  function ensureSiteInScope(scope: readonly string[]): void {
    const first = scope[0]
    if (first === undefined) return
    if (siteId.value !== null && scope.includes(siteId.value)) return
    setSite(first)
  }

  function applyLocale(next: UiLocale): void {
    locale.value = next
    setLocale(next)
    writeStored(LOCALE_KEY, next)
    void syncToProfile({ locale: next })
  }

  function toggleLocale(): void {
    applyLocale(locale.value === 'en' ? 'dv' : 'en')
  }

  // No sync call here: the watcher below covers every path that changes the
  // theme, including `cycleTheme` and a change made in another tab.
  function applyTheme(next: UiThemePreference): void {
    setTheme(next)
  }

  async function syncToProfile(patch: Record<string, string>): Promise<void> {
    if (!PROFILE_SYNC_ENABLED) return
    try {
      await api.patch('/me/preferences', patch)
    } catch {
      // A preference that failed to reach the server still applies locally.
      // Interrupting a user's work to tell them their theme did not sync would
      // be worse than the silent divergence.
    }
  }

  // `cycleTheme` is exposed for the keyboard shortcut and the toggle button;
  // watching the composable's own ref keeps the profile sync working no matter
  // which of the two paths changed it.
  watch(theme, (next) => void syncToProfile({ theme: next }))

  return {
    theme,
    resolvedTheme: resolved,
    isDark,
    applyTheme,
    cycleTheme,
    locale,
    applyLocale,
    toggleLocale,
    siteId,
    siteFilter,
    setSite,
    ensureSiteInScope,
  }
})
